// src/utils/apiClient.js
import React from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// Global auth context reference (set by useApiClient hook)
let authContextRef = null;

export const setAuthContext = (authContext) => {
  authContextRef = authContext;
};

// Enhanced fetch wrapper with automatic token handling
export const apiClient = async (url, options = {}) => {
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Build full URL
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  
  // Default headers
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  // Merge headers
  const finalOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(fullUrl, finalOptions);
    
    // Handle 401 Unauthorized (expired token)
    if (response.status === 401) {
      console.warn('🔒 Token expired or invalid, logging out user');
      
      // Clear auth state
      if (authContextRef) {
        authContextRef.logout();
      } else {
        // Fallback if context not available
        localStorage.removeItem('token');
        window.location.href = '/login?reason=expired';
      }
      
      // Throw error to prevent further processing
      throw new Error('Authentication expired');
    }
    
    // Handle other HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    
    return response;
    
  } catch (error) {
    // Network errors, parsing errors, etc.
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection');
    }
    throw error;
  }
};

// Convenience methods
export const apiGet = (url, options = {}) => 
  apiClient(url, { ...options, method: 'GET' });

export const apiPost = (url, data, options = {}) => 
  apiClient(url, { 
    ...options, 
    method: 'POST', 
    body: JSON.stringify(data) 
  });

export const apiPut = (url, data, options = {}) => 
  apiClient(url, { 
    ...options, 
    method: 'PUT', 
    body: JSON.stringify(data) 
  });

export const apiDelete = (url, options = {}) => 
  apiClient(url, { ...options, method: 'DELETE' });

// Hook to connect auth context
export const useApiClient = (authContext) => {
  React.useEffect(() => {
    setAuthContext(authContext);
  }, [authContext]);
  
  return { apiClient, apiGet, apiPost, apiPut, apiDelete };
};